import { Option, Schema } from "effect";

export const OfficeBoundaryParseOptions = {
  errors: "all",
  onExcessProperty: "error",
  reportInput: false,
} as const;

const CanonicalTextSchema = Schema.Trim.check(
  Schema.isMinLength(1),
  Schema.makeFilter(
    (value) =>
      Array.from(value).every((character) => {
        const codePoint = character.codePointAt(0);
        return codePoint !== undefined && codePoint > 31 && codePoint !== 127;
      }),
    { expected: "trimmed text without control characters" },
  ),
);
const CanonicalIdSchema = CanonicalTextSchema.check(Schema.isMaxLength(160));
export const OfficeCanonicalIdSchema = CanonicalIdSchema;
const TitleSchema = CanonicalTextSchema.check(Schema.isMaxLength(512));
const ShortTextSchema = Schema.String.check(Schema.isMaxLength(2_000));
const RichTextSchema = Schema.String.check(Schema.isMaxLength(100_000));
export const OFFICE_PRESENTATION_TEXT_BOX_MAX_CHARACTERS = 32_000;
export const OFFICE_PRESENTATION_COORDINATE_LIMIT_EMU = 100_000_000;
export const OFFICE_PRESENTATION_FONT_SIZE_MIN_PT = 6;
export const OFFICE_PRESENTATION_FONT_SIZE_MAX_PT = 400;
export const OFFICE_PRESENTATION_ANIMATION_MAX_ITEMS = 256;
export const OFFICE_PRESENTATION_ANIMATION_TIME_MAX_MS = 600_000;
export const OFFICE_PRESENTATION_MOTION_PATH_MAX_CHARACTERS = 4_096;
export const OFFICE_PRESENTATION_DECK_TRANSITION_MAX_SLIDES = 10_000;
export const OFFICE_PRESENTATION_PICTURE_MAX_BYTES = 5 * 1_048_576;
export const OFFICE_PRESENTATION_TABLE_DIMENSION_MAXIMUM = 32;
export const OfficePresentationPresetShapeKinds = [
  "rect",
  "roundRect",
  "ellipse",
  "triangle",
  "rtTriangle",
  "diamond",
  "parallelogram",
  "trapezoid",
  "pentagon",
  "hexagon",
  "heptagon",
  "octagon",
  "chevron",
  "rightArrow",
  "leftArrow",
  "upArrow",
  "downArrow",
  "star5",
  "heart",
  "plus",
  "can",
  "cube",
  "foldedCorner",
] as const;
export const OFFICE_SPREADSHEET_FREEZE_COUNT_MAXIMUM = 100;
export const OFFICE_SPREADSHEET_HYPERLINK_TARGET_MAX_CHARACTERS = 2_048;
export const OFFICE_SPREADSHEET_DATA_VALIDATION_MAX_CELLS = 65_536;
export const OFFICE_SPREADSHEET_DATA_VALIDATION_LIST_MAX_VALUES = 32;
export const OFFICE_SPREADSHEET_DATA_VALIDATION_LIST_VALUE_MAX_CHARACTERS = 64;
export const OFFICE_SPREADSHEET_DATA_VALIDATION_LIST_FORMULA_MAX_CHARACTERS = 255;
export const OFFICE_SPREADSHEET_SPARKLINE_MAX_ROWS = 200;
export const OFFICE_SPREADSHEET_SPARKLINE_MAX_COLUMNS = 64;
export const OFFICE_SPREADSHEET_CONDITIONAL_FORMAT_MAX_CELLS = 65_536;
export const OFFICE_PDF_HIGHLIGHT_MAX_RECTS = 64;
export const OFFICE_PDF_HIGHLIGHT_COORDINATE_LIMIT_PT = 100_000;
export const OFFICE_PDF_HIGHLIGHT_OPACITY_MINIMUM = 0.1;
export const OFFICE_PDF_NOTE_CONTENTS_MAX_CHARACTERS = 2_000;
export const OFFICE_PDF_NOTE_ICON_WIDTH_PT = 20;
export const OFFICE_PDF_NOTE_ICON_HEIGHT_PT = 18;
export const OFFICE_DOCUMENT_HYPERLINK_TARGET_MAX_CHARACTERS = 2_048;
export const OFFICE_DOCUMENT_INDENT_MIN_TWIPS = -14_400;
export const OFFICE_DOCUMENT_INDENT_MAX_TWIPS = 14_400;
export const OFFICE_DOCUMENT_SPACING_MIN_TWIPS = 0;
export const OFFICE_DOCUMENT_SPACING_MAX_TWIPS = 31_680;
export const OFFICE_DOCUMENT_LINE_SPACING_MIN = 0.5;
export const OFFICE_DOCUMENT_LINE_SPACING_MAX = 6;
export const OFFICE_PDF_FORM_FIELD_NAME_MAX_CHARACTERS = 255;
export const OFFICE_PDF_FORM_FIELD_VALUE_MAX_CHARACTERS = 2_000;
export const OFFICE_MARKDOWN_FRONTMATTER_MAX_CHARACTERS = 8_000;
const NonNegativeIntegerSchema = Schema.Int.check(
  Schema.isGreaterThanOrEqualTo(0),
  Schema.isLessThanOrEqualTo(Number.MAX_SAFE_INTEGER),
);
const DocumentTextOffsetSchema = Schema.Int.check(
  Schema.isGreaterThanOrEqualTo(0),
  Schema.isLessThanOrEqualTo(100_000),
);
export const OFFICE_DOCUMENT_TABLE_INDEX_MAXIMUM = 9_999;
export const OFFICE_DOCUMENT_TABLE_ROW_COUNT_MAXIMUM = 50;
export const OFFICE_DOCUMENT_TABLE_COLUMN_COUNT_MAXIMUM = 20;
export const OFFICE_DOCUMENT_TABLE_CELL_MAX_PARAGRAPHS = 256;
export const OFFICE_DOCUMENT_TABLE_CELL_MAX_TEXT_CHARACTERS = 100_000;
export const OfficeDocumentTableIndexSchema = Schema.Int.check(
  Schema.isBetween({ minimum: 0, maximum: OFFICE_DOCUMENT_TABLE_INDEX_MAXIMUM }),
);
const OfficeDocumentTableRowCountSchema = Schema.Int.check(
  Schema.isBetween({ minimum: 1, maximum: OFFICE_DOCUMENT_TABLE_ROW_COUNT_MAXIMUM }),
);
const OfficeDocumentTableColumnCountSchema = Schema.Int.check(
  Schema.isBetween({ minimum: 1, maximum: OFFICE_DOCUMENT_TABLE_COLUMN_COUNT_MAXIMUM }),
);
export function isOfficeDocumentTableCellParagraphText(value: string): boolean {
  return Array.from(value).every((character) => {
    const codePoint = character.codePointAt(0);
    return (
      codePoint !== undefined &&
      codePoint >= 0x20 &&
      codePoint !== 0x7f &&
      codePoint !== 0xfffe &&
      codePoint !== 0xffff &&
      !(codePoint >= 0xd800 && codePoint <= 0xdfff)
    );
  });
}

export function officeDocumentTableCellTextLength(paragraphs: readonly string[]): number {
  return (
    paragraphs.reduce((total, paragraph) => total + paragraph.length, 0) +
    Math.max(0, paragraphs.length - 1)
  );
}

export function isOfficeDocumentTableCellParagraphs(paragraphs: readonly string[]): boolean {
  return (
    paragraphs.length >= 1 &&
    paragraphs.length <= OFFICE_DOCUMENT_TABLE_CELL_MAX_PARAGRAPHS &&
    paragraphs.every(isOfficeDocumentTableCellParagraphText) &&
    officeDocumentTableCellTextLength(paragraphs) <= OFFICE_DOCUMENT_TABLE_CELL_MAX_TEXT_CHARACTERS
  );
}

const OfficeDocumentTableCellParagraphSchema = Schema.String.check(
  Schema.isMaxLength(OFFICE_DOCUMENT_TABLE_CELL_MAX_TEXT_CHARACTERS),
  Schema.makeFilter(
    (value) =>
      isOfficeDocumentTableCellParagraphText(value) ||
      "table cell paragraphs must contain XML-compatible text without controls or newlines",
  ),
);
export const OfficeDocumentTableCellParagraphsSchema = Schema.Array(
  OfficeDocumentTableCellParagraphSchema,
).check(
  Schema.isMinLength(1),
  Schema.isMaxLength(OFFICE_DOCUMENT_TABLE_CELL_MAX_PARAGRAPHS),
  Schema.makeFilter(
    (paragraphs) =>
      officeDocumentTableCellTextLength(paragraphs) <=
        OFFICE_DOCUMENT_TABLE_CELL_MAX_TEXT_CHARACTERS ||
      "table cell text must fit the bounded native text limit",
  ),
);
const FiniteCoordinateSchema = Schema.Finite.check(
  Schema.isGreaterThanOrEqualTo(-OFFICE_PRESENTATION_COORDINATE_LIMIT_EMU),
  Schema.isLessThanOrEqualTo(OFFICE_PRESENTATION_COORDINATE_LIMIT_EMU),
);
const FileNameSchema = CanonicalTextSchema.check(
  Schema.isMaxLength(255),
  Schema.makeFilter(
    (value) =>
      value !== "." &&
      value !== ".." &&
      !value.includes("/") &&
      !value.includes("\\") &&
      !value.includes("\u0000"),
    { expected: "a base filename without path separators" },
  ),
);

export const OfficeArtifactKinds = [
  "document",
  "presentation",
  "spreadsheet",
  "pdf",
  "markdown",
] as const;
export const OfficeArtifactKindSchema = Schema.Literals(OfficeArtifactKinds);
export const OfficeNativeMediaTypeSchema = Schema.Literals([
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/pdf",
  "text/markdown",
]);
export const OfficeEngineSchema = Schema.Literals([
  "genoffice-docx",
  "genoffice-pptx",
  "genoffice-xlsx",
  "genoffice-pdf",
  "genoffice-markdown",
]);

export const OfficeRevisionPayloadSchema = Schema.Struct({
  schemaVersion: Schema.Literal(1),
  artifactId: CanonicalIdSchema,
  revisionId: CanonicalIdSchema,
  kind: OfficeArtifactKindSchema,
  nativeMediaType: OfficeNativeMediaTypeSchema,
  nativeObjectId: CanonicalIdSchema,
  normalizedObjectId: CanonicalIdSchema,
  attachmentObjectIds: Schema.Array(CanonicalIdSchema).check(Schema.isMaxLength(1_024)),
  engine: OfficeEngineSchema,
  engineVersion: CanonicalTextSchema.check(Schema.isMaxLength(120)),
  sourceRevisionId: Schema.optionalKey(CanonicalIdSchema),
});

export const OfficePdfPageNumberSchema = Schema.Int.check(
  Schema.isBetween({ minimum: 1, maximum: 2_000 }),
);
const OFFICE_PDF_OBJECT_NUMBER_MAXIMUM = 2_147_483_647;
const OFFICE_PDF_GENERATION_NUMBER_MAXIMUM = 65_535;
const OfficePdfObjectNumberSchema = Schema.Int.check(
  Schema.isBetween({ minimum: 1, maximum: OFFICE_PDF_OBJECT_NUMBER_MAXIMUM }),
);
const OfficePdfGenerationNumberSchema = Schema.Int.check(
  Schema.isBetween({ minimum: 0, maximum: OFFICE_PDF_GENERATION_NUMBER_MAXIMUM }),
);
export const OfficePdfNativePageRefSchema = Schema.Struct({
  num: OfficePdfObjectNumberSchema,
  gen: OfficePdfGenerationNumberSchema,
});
const OFFICE_PDF_PAGE_ID_PATTERN = /^pdf-ref:([1-9][0-9]*):(0|[1-9][0-9]*)$/u;

function pdfPageRefFromId(
  pageId: string,
): { readonly num: number; readonly gen: number } | undefined {
  const match = OFFICE_PDF_PAGE_ID_PATTERN.exec(pageId);
  if (!match) return undefined;
  const num = Number(match[1]);
  const gen = Number(match[2]);
  if (
    !Number.isSafeInteger(num) ||
    num < 1 ||
    num > OFFICE_PDF_OBJECT_NUMBER_MAXIMUM ||
    !Number.isSafeInteger(gen) ||
    gen < 0 ||
    gen > OFFICE_PDF_GENERATION_NUMBER_MAXIMUM
  ) {
    return undefined;
  }
  return { num, gen };
}

export const OfficePdfPageIdSchema = CanonicalIdSchema.check(
  Schema.makeFilter(
    (pageId) =>
      pdfPageRefFromId(pageId) !== undefined ||
      "a canonical PDF indirect page reference in pdf-ref:<num>:<gen> form",
  ),
).pipe(Schema.brand("OfficePdfPageId"));
const OfficePdfTextSelectionSchema = Schema.Struct({
  kind: Schema.Literal("pdf-text"),
  startPage: OfficePdfPageNumberSchema,
  endPage: OfficePdfPageNumberSchema,
  selectedText: RichTextSchema.check(Schema.isMinLength(1)),
}).check(
  Schema.makeFilter(
    (selection) =>
      (selection.endPage >= selection.startPage && selection.endPage - selection.startPage < 256) ||
      "PDF text selection pages must be ordered and span no more than 256 pages",
  ),
);

const OfficeSelectionIdsSchema = Schema.Array(CanonicalIdSchema).check(
  Schema.isMinLength(1),
  Schema.isMaxLength(256),
);
const OfficeDocumentTextSelectionFields = {
  kind: Schema.Literal("document-text"),
  blockIds: OfficeSelectionIdsSchema,
  start: DocumentTextOffsetSchema,
  end: DocumentTextOffsetSchema,
} as const;
const OfficeDocumentTextSelectionTargetSchema = Schema.Struct({
  kind: Schema.Literal("document-text"),
  blockId: CanonicalIdSchema,
  start: DocumentTextOffsetSchema,
  end: DocumentTextOffsetSchema,
}).check(
  Schema.makeFilter(
    (selection) =>
      selection.start <= selection.end || "selection start must not exceed selection end",
  ),
);
const OfficeDocumentTableCellTextSelectionFields = {
  kind: Schema.Literal("document-table-cell-text"),
  blockId: CanonicalIdSchema,
  rowIndex: OfficeDocumentTableIndexSchema,
  cellIndex: OfficeDocumentTableIndexSchema,
  start: DocumentTextOffsetSchema,
  end: DocumentTextOffsetSchema,
} as const;
const OfficeDocumentTableCellTextSelectionTargetSchema = Schema.Struct(
  OfficeDocumentTableCellTextSelectionFields,
).check(
  Schema.makeFilter(
    (selection) =>
      selection.start <= selection.end || "selection start must not exceed selection end",
  ),
);
const OfficePresentationElementsSelectionSchema = Schema.Struct({
  kind: Schema.Literal("presentation-elements"),
  slideId: CanonicalIdSchema,
  elementIds: OfficeSelectionIdsSchema,
});
export const OfficeSpreadsheetSelectionRangeSchema = CanonicalTextSchema.check(
  Schema.isMaxLength(128),
  Schema.makeFilter((value) => /^(?:'[^']+'|[A-Za-z0-9_. -]+)![A-Z]+\d+:[A-Z]+\d+$/u.test(value), {
    expected: "a bounded sheet-qualified A1 range",
  }),
);
const OfficeSpreadsheetRangeSelectionSchema = Schema.Struct({
  kind: Schema.Literal("spreadsheet-range"),
  sheetId: CanonicalIdSchema,
  range: OfficeSpreadsheetSelectionRangeSchema,
});
const OfficeMarkdownTextSelectionFields = {
  kind: Schema.Literal("markdown-text"),
  blockIds: OfficeSelectionIdsSchema,
  start: NonNegativeIntegerSchema,
  end: NonNegativeIntegerSchema,
} as const;
const OfficeMarkdownTextSelectionTargetSchema = Schema.Struct({
  kind: Schema.Literal("markdown-text"),
  blockId: CanonicalIdSchema,
  start: NonNegativeIntegerSchema,
  end: NonNegativeIntegerSchema,
}).check(
  Schema.makeFilter(
    (selection) =>
      selection.start <= selection.end || "selection start must not exceed selection end",
  ),
);
const OfficePdfTextSelectionTargetSchema = Schema.Struct({
  kind: Schema.Literal("pdf-text"),
  startPage: OfficePdfPageNumberSchema,
  endPage: OfficePdfPageNumberSchema,
  readOnly: Schema.Literal(true),
}).check(
  Schema.makeFilter(
    (selection) =>
      (selection.endPage >= selection.startPage && selection.endPage - selection.startPage < 256) ||
      "PDF text selection pages must be ordered and span no more than 256 pages",
  ),
);

export const OfficeSelectionTargetSchema = Schema.Union([
  OfficeDocumentTextSelectionTargetSchema,
  OfficeDocumentTableCellTextSelectionTargetSchema,
  OfficePresentationElementsSelectionSchema,
  OfficeSpreadsheetRangeSelectionSchema,
  OfficeMarkdownTextSelectionTargetSchema,
  OfficePdfTextSelectionTargetSchema,
]);

export const OfficeSelectionSchema = Schema.Union([
  Schema.Struct({
    ...OfficeDocumentTextSelectionFields,
    selectedText: RichTextSchema,
  }).check(
    Schema.makeFilter(
      (selection) =>
        selection.start <= selection.end || "selection start must not exceed selection end",
    ),
  ),
  Schema.Struct({
    ...OfficeDocumentTableCellTextSelectionFields,
    selectedText: RichTextSchema,
  }).check(
    Schema.makeFilter(
      (selection) =>
        selection.start <= selection.end || "selection start must not exceed selection end",
    ),
  ),
  OfficePresentationElementsSelectionSchema,
  OfficeSpreadsheetRangeSelectionSchema,
  Schema.Struct({
    kind: Schema.Literal("pdf-region"),
    pageId: OfficePdfPageIdSchema,
    regionIds: OfficeSelectionIdsSchema,
  }),
  OfficePdfTextSelectionSchema,
  Schema.Struct({
    ...OfficeMarkdownTextSelectionFields,
    selectedText: RichTextSchema,
  }).check(
    Schema.makeFilter(
      (selection) =>
        selection.start <= selection.end || "selection start must not exceed selection end",
    ),
  ),
]);

const DocumentInsertFields = {
  type: Schema.Literal("document.block.insert"),
  blockId: CanonicalIdSchema,
  afterBlockId: Schema.optionalKey(CanonicalIdSchema),
} as const;
export const OfficeDocumentAlignmentSchema = Schema.Literals([
  "left",
  "center",
  "right",
  "justify",
  "distribute",
]);
export const OfficeDocumentInlineMarkSchema = Schema.Literals([
  "bold",
  "italic",
  "underline",
  "strikethrough",
]);
export const OfficeDocumentVerticalAlignmentSchema = Schema.Literals(["superscript", "subscript"]);
export const OFFICE_DOCUMENT_FONT_SIZE_MIN_PT = 6;
export const OFFICE_DOCUMENT_FONT_SIZE_MAX_PT = 400;
export const OFFICE_DOCUMENT_PAGE_DIMENSION_MIN_TWIPS = 720;
export const OFFICE_DOCUMENT_PAGE_DIMENSION_MAX_TWIPS = 31_680;
export const OFFICE_DOCUMENT_PAGE_MARGIN_MIN_TWIPS = -14_400;
export const OFFICE_DOCUMENT_PAGE_MARGIN_MAX_TWIPS = 14_400;
export const OFFICE_DOCUMENT_HEADER_FOOTER_TEXT_MAX_CHARACTERS = 2_000;
export const OfficeDocumentTextColorSchema = Schema.String.check(
  Schema.makeFilter((value) => /^[0-9A-F]{6}$/u.test(value), {
    expected: "an uppercase six-digit sRGB color without a leading #",
  }),
);
export const OfficeDocumentHighlightSchema = Schema.Literals([
  "black",
  "blue",
  "cyan",
  "green",
  "magenta",
  "red",
  "yellow",
  "white",
  "darkBlue",
  "darkCyan",
  "darkGreen",
  "darkMagenta",
  "darkRed",
  "darkYellow",
  "darkGray",
  "lightGray",
]);
export const OfficeDocumentFontSizePtSchema = Schema.Finite.check(
  Schema.isBetween({
    minimum: OFFICE_DOCUMENT_FONT_SIZE_MIN_PT,
    maximum: OFFICE_DOCUMENT_FONT_SIZE_MAX_PT,
  }),
  Schema.makeFilter((value) => Number.isInteger(value * 2) || "a half-point font-size increment"),
);
export const OfficeFontFamilySchema = CanonicalTextSchema.check(Schema.isMaxLength(128));
export const isOfficeFontFamily = Schema.is(OfficeFontFamilySchema);
export const OfficeDocumentTextFormatPatchSchema = Schema.Struct({
  fontSizePt: Schema.optionalKey(Schema.Union([OfficeDocumentFontSizePtSchema, Schema.Null])),
  textColor: Schema.optionalKey(Schema.Union([OfficeDocumentTextColorSchema, Schema.Null])),
  highlight: Schema.optionalKey(Schema.Union([OfficeDocumentHighlightSchema, Schema.Null])),
  verticalAlignment: Schema.optionalKey(
    Schema.Union([OfficeDocumentVerticalAlignmentSchema, Schema.Null]),
  ),
  // The current document UI edits Word's ascii and hAnsi slots together. Projection
  // keeps their effective families distinct and disables this one mutation when
  // their direct authorities diverge, so the combined write never flattens silently.
  latinFontFamily: Schema.optionalKey(Schema.Union([OfficeFontFamilySchema, Schema.Null])),
  eastAsianFontFamily: Schema.optionalKey(
    Schema.Union([OfficeFontFamilySchema, Schema.Null]),
  ),
  complexScriptFontFamily: Schema.optionalKey(
    Schema.Union([OfficeFontFamilySchema, Schema.Null]),
  ),
}).check(
  Schema.makeFilter(
    (format) =>
      format.fontSizePt !== undefined ||
      format.textColor !== undefined ||
      format.highlight !== undefined ||
      format.verticalAlignment !== undefined ||
      format.latinFontFamily !== undefined ||
      format.eastAsianFontFamily !== undefined ||
      format.complexScriptFontFamily !== undefined ||
      "a document text format patch must change at least one supported property",
  ),
);
const OfficeDocumentPageDimensionTwipsSchema = Schema.Int.check(
  Schema.isBetween({
    minimum: OFFICE_DOCUMENT_PAGE_DIMENSION_MIN_TWIPS,
    maximum: OFFICE_DOCUMENT_PAGE_DIMENSION_MAX_TWIPS,
  }),
);
const OfficeDocumentPageMarginTwipsSchema = Schema.Int.check(
  Schema.isBetween({
    minimum: OFFICE_DOCUMENT_PAGE_MARGIN_MIN_TWIPS,
    maximum: OFFICE_DOCUMENT_PAGE_MARGIN_MAX_TWIPS,
  }),
);
export const OfficeDocumentPageSetupSchema = Schema.Struct({
  pageWidthTwips: OfficeDocumentPageDimensionTwipsSchema,
  pageHeightTwips: OfficeDocumentPageDimensionTwipsSchema,
  orientation: Schema.Literals(["portrait", "landscape"]),
  marginTopTwips: OfficeDocumentPageMarginTwipsSchema,
  marginRightTwips: OfficeDocumentPageMarginTwipsSchema,
  marginBottomTwips: OfficeDocumentPageMarginTwipsSchema,
  marginLeftTwips: OfficeDocumentPageMarginTwipsSchema,
});
export const OfficeDocumentHeaderFooterTextSchema = Schema.String.check(
  Schema.isMaxLength(OFFICE_DOCUMENT_HEADER_FOOTER_TEXT_MAX_CHARACTERS),
);
const DocumentInlineFormatOperationSchema = Schema.Struct({
  type: Schema.Literal("document.inline.set-format"),
  blockId: CanonicalIdSchema,
  start: DocumentTextOffsetSchema,
  end: DocumentTextOffsetSchema,
  mark: OfficeDocumentInlineMarkSchema,
  enabled: Schema.Boolean,
}).check(
  Schema.makeFilter(
    (operation) => operation.end > operation.start || "end must be greater than start",
  ),
);
const DocumentInlineTextFormatOperationSchema = Schema.Struct({
  type: Schema.Literal("document.inline.set-text-format"),
  blockId: CanonicalIdSchema,
  start: DocumentTextOffsetSchema,
  end: DocumentTextOffsetSchema,
  format: OfficeDocumentTextFormatPatchSchema,
}).check(
  Schema.makeFilter(
    (operation) => operation.end > operation.start || "end must be greater than start",
  ),
);

const OfficeDocumentIndentTwipsSchema = Schema.Int.check(
  Schema.isBetween({
    minimum: OFFICE_DOCUMENT_INDENT_MIN_TWIPS,
    maximum: OFFICE_DOCUMENT_INDENT_MAX_TWIPS,
  }),
);
const OfficeDocumentSpacingTwipsSchema = Schema.Int.check(
  Schema.isBetween({
    minimum: OFFICE_DOCUMENT_SPACING_MIN_TWIPS,
    maximum: OFFICE_DOCUMENT_SPACING_MAX_TWIPS,
  }),
);
export const OfficeDocumentLineSpacingSchema = Schema.Finite.check(
  Schema.isBetween({
    minimum: OFFICE_DOCUMENT_LINE_SPACING_MIN,
    maximum: OFFICE_DOCUMENT_LINE_SPACING_MAX,
  }),
);
export const OfficeDocumentParagraphFormatPatchSchema = Schema.Struct({
  indentLeftTwips: Schema.optionalKey(Schema.Union([OfficeDocumentIndentTwipsSchema, Schema.Null])),
  indentRightTwips: Schema.optionalKey(
    Schema.Union([OfficeDocumentIndentTwipsSchema, Schema.Null]),
  ),
  indentFirstLineTwips: Schema.optionalKey(
    Schema.Union([OfficeDocumentIndentTwipsSchema, Schema.Null]),
  ),
  spaceBeforeTwips: Schema.optionalKey(
    Schema.Union([OfficeDocumentSpacingTwipsSchema, Schema.Null]),
  ),
  spaceAfterTwips: Schema.optionalKey(Schema.Union([OfficeDocumentSpacingTwipsSchema, Schema.Null])),
  lineSpacing: Schema.optionalKey(Schema.Union([OfficeDocumentLineSpacingSchema, Schema.Null])),
}).check(
  Schema.makeFilter(
    (format) =>
      format.indentLeftTwips !== undefined ||
      format.indentRightTwips !== undefined ||
      format.indentFirstLineTwips !== undefined ||
      format.spaceBeforeTwips !== undefined ||
      format.spaceAfterTwips !== undefined ||
      format.lineSpacing !== undefined ||
      "a paragraph format patch must change at least one supported property",
  ),
);

const decodeDocumentHyperlinkUrl = Schema.decodeUnknownOption(Schema.URLFromString);

function documentExternalHyperlinkTargetIssue(value: string): true | string {
  const escapedLength = value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&apos;").length;
  if (escapedLength > OFFICE_DOCUMENT_HYPERLINK_TARGET_MAX_CHARACTERS) {
    return "a hyperlink that fits the native OOXML target bound after encoding";
  }
  const decoded = decodeDocumentHyperlinkUrl(value);
  if (Option.isNone(decoded)) return "an absolute HTTP(S) or mailto hyperlink";
  const url = decoded.value;
  if (url.protocol === "mailto:") {
    return url.pathname.length > 0 || "a mailto hyperlink with a recipient";
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") {
    return "an HTTP(S) or mailto hyperlink";
  }
  return (
    (url.hostname !== "" && url.username === "" && url.password === "") ||
    "an HTTP(S) hyperlink without userinfo"
  );
}

export const OfficeDocumentHyperlinkAnchorSchema = CanonicalTextSchema.check(
  Schema.isMaxLength(40),
  Schema.makeFilter(
    (value) =>
      /^[A-Za-z_][A-Za-z0-9._-]*$/u.test(value) || "an internal Word bookmark-style anchor",
  ),
);
export const OfficeDocumentHyperlinkTargetSchema = Schema.Union([
  Schema.Struct({
    kind: Schema.Literal("external"),
    url: CanonicalTextSchema.check(
      Schema.isMaxLength(OFFICE_DOCUMENT_HYPERLINK_TARGET_MAX_CHARACTERS),
      Schema.makeFilter(documentExternalHyperlinkTargetIssue),
    ),
  }),
  Schema.Struct({
    kind: Schema.Literal("internal"),
    anchor: OfficeDocumentHyperlinkAnchorSchema,
  }),
]);
export const isOfficeDocumentHyperlinkTarget = Schema.is(OfficeDocumentHyperlinkTargetSchema);
const DocumentHyperlinkOperationSchema = Schema.Struct({
  type: Schema.Literal("document.hyperlink.set"),
  blockId: CanonicalIdSchema,
  start: DocumentTextOffsetSchema,
  end: DocumentTextOffsetSchema,
  target: Schema.Union([OfficeDocumentHyperlinkTargetSchema, Schema.Null]),
}).check(
  Schema.makeFilter(
    (operation) => operation.end > operation.start || "end must be greater than start",
  ),
);

const DocumentOperationSchema = Schema.Union([
  Schema.Struct({
    ...DocumentInsertFields,
    blockType: Schema.Literals(["paragraph", "quote", "code", "callout"]),
    text: RichTextSchema,
    styleId: Schema.optionalKey(CanonicalIdSchema),
  }),
  Schema.Struct({
    ...DocumentInsertFields,
    blockType: Schema.Literal("heading"),
    headingLevel: Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 9 })),
    text: RichTextSchema,
    styleId: Schema.optionalKey(CanonicalIdSchema),
  }),
  Schema.Struct({
    ...DocumentInsertFields,
    blockType: Schema.Literal("list-item"),
    listKind: Schema.Literals(["bullet", "ordered"]),
    level: Schema.Int.check(Schema.isBetween({ minimum: 0, maximum: 8 })),
    text: RichTextSchema,
    styleId: Schema.optionalKey(CanonicalIdSchema),
  }),
  Schema.Struct({
    ...DocumentInsertFields,
    blockType: Schema.Literal("page-break"),
  }),
  Schema.Struct({
    ...DocumentInsertFields,
    blockType: Schema.Literal("table"),
    rowCount: OfficeDocumentTableRowCountSchema,
    columnCount: OfficeDocumentTableColumnCountSchema,
  }),
  Schema.Struct({
    type: Schema.Literal("document.block.replace-text"),
    blockId: CanonicalIdSchema,
    text: RichTextSchema,
  }),
  Schema.Struct({
    type: Schema.Literal("document.table.cell.replace-text"),
    blockId: CanonicalIdSchema,
    rowIndex: OfficeDocumentTableIndexSchema,
    cellIndex: OfficeDocumentTableIndexSchema,
    paragraphs: OfficeDocumentTableCellParagraphsSchema,
  }),
  Schema.Struct({
    type: Schema.Literals(["document.table.insert-row", "document.table.delete-row"]),
    blockId: CanonicalIdSchema,
    index: OfficeDocumentTableIndexSchema,
  }),
  Schema.Struct({
    type: Schema.Literals(["document.table.insert-column", "document.table.delete-column"]),
    blockId: CanonicalIdSchema,
    index: OfficeDocumentTableIndexSchema,
  }),
  Schema.Struct({ type: Schema.Literal("document.block.delete"), blockId: CanonicalIdSchema }),
  Schema.Struct({
    type: Schema.Literal("document.block.move"),
    blockId: CanonicalIdSchema,
    afterBlockId: Schema.optionalKey(CanonicalIdSchema),
  }),
  Schema.Struct({
    type: Schema.Literal("document.block.set-style"),
    blockId: CanonicalIdSchema,
    styleId: CanonicalIdSchema,
  }),
  Schema.Struct({
    type: Schema.Literal("document.block.set-alignment"),
    blockId: CanonicalIdSchema,
    alignment: OfficeDocumentAlignmentSchema,
  }),
  DocumentInlineFormatOperationSchema,
  DocumentInlineTextFormatOperationSchema,
  Schema.Struct({
    type: Schema.Literal("document.page.set-setup"),
    setup: OfficeDocumentPageSetupSchema,
  }).check(
    Schema.makeFilter(
      (operation) =>
        operation.setup.marginLeftTwips + operation.setup.marginRightTwips <
          operation.setup.pageWidthTwips ||
        "horizontal page margins must leave a positive text width",
    ),
    Schema.makeFilter(
      (operation) =>
        operation.setup.marginTopTwips + operation.setup.marginBottomTwips <
          operation.setup.pageHeightTwips ||
        "vertical page margins must leave a positive text height",
    ),
    Schema.makeFilter(
      (operation) =>
        (operation.setup.orientation === "portrait"
          ? operation.setup.pageHeightTwips >= operation.setup.pageWidthTwips
          : operation.setup.pageWidthTwips >= operation.setup.pageHeightTwips) ||
        "page dimensions must agree with the declared orientation",
    ),
  ),
  Schema.Struct({
    type: Schema.Literal("document.header-footer.set"),
    headerText: Schema.optionalKey(OfficeDocumentHeaderFooterTextSchema),
    footerText: Schema.optionalKey(OfficeDocumentHeaderFooterTextSchema),
    footerPageNumber: Schema.optionalKey(Schema.Boolean),
  }).check(
    Schema.makeFilter(
      (operation) =>
        operation.headerText !== undefined ||
        operation.footerText !== undefined ||
        operation.footerPageNumber !== undefined ||
        "a header/footer operation must change at least one supported property",
    ),
  ),
  Schema.Struct({
    type: Schema.Literal("document.comment.add"),
    blockId: CanonicalIdSchema,
    commentId: CanonicalIdSchema,
    text: ShortTextSchema,
  }),
  Schema.Struct({
    type: Schema.Literal("document.comment.reply"),
    parentCommentId: CanonicalIdSchema,
    commentId: CanonicalIdSchema,
    text: ShortTextSchema,
  }),
  Schema.Struct({
    type: Schema.Literal("document.comment.set-resolved"),
    commentId: CanonicalIdSchema,
    resolved: Schema.Boolean,
  }),
  Schema.Struct({
    type: Schema.Literal("document.comment.delete"),
    commentId: CanonicalIdSchema,
  }),
  DocumentHyperlinkOperationSchema,
  Schema.Struct({
    type: Schema.Literal("document.block.set-paragraph-format"),
    blockId: CanonicalIdSchema,
    format: OfficeDocumentParagraphFormatPatchSchema,
  }),
  Schema.Struct({
    type: Schema.Literal("document.block.set-list-level"),
    blockId: CanonicalIdSchema,
    level: Schema.Int.check(Schema.isBetween({ minimum: 0, maximum: 8 })),
  }),
]);

/** Presentation geometry is expressed in OOXML EMUs; rotation is in degrees. */
const SlideTransformSchema = Schema.Struct({
  x: FiniteCoordinateSchema,
  y: FiniteCoordinateSchema,
  width: FiniteCoordinateSchema.check(Schema.isGreaterThan(0)),
  height: FiniteCoordinateSchema.check(Schema.isGreaterThan(0)),
  rotation: Schema.Finite.check(Schema.isBetween({ minimum: -360, maximum: 360 })),
});
const PresentationTextBoxTextSchema = Schema.String.check(
  Schema.isMaxLength(OFFICE_PRESENTATION_TEXT_BOX_MAX_CHARACTERS),
  Schema.makeFilter(
    (value) =>
      Array.from(value).every((character) => {
        const codePoint = character.codePointAt(0);
        return (
          codePoint === 0x09 ||
          codePoint === 0x0a ||
          codePoint === 0x0d ||
          (codePoint !== undefined &&
            ((codePoint >= 0x20 && codePoint <= 0xd7ff) ||
              (codePoint >= 0xe000 && codePoint <= 0xfffd) ||
              (codePoint >= 0x10000 && codePoint <= 0x10ffff)))
        );
      }),
    { expected: "XML-compatible presentation text" },
  ),
);
export const OfficePresentationTextAlignmentSchema = Schema.Literals([
  "left",
  "center",
  "right",
  "justify",
]);
export const OfficePresentationTextFormatPatchSchema = Schema.Struct({
  bold: Schema.optionalKey(Schema.Boolean),
  italic: Schema.optionalKey(Schema.Boolean),
  underline: Schema.optionalKey(Schema.Boolean),
  fontSizePt: Schema.optionalKey(
    Schema.Finite.check(
      Schema.isBetween({
        minimum: OFFICE_PRESENTATION_FONT_SIZE_MIN_PT,
        maximum: OFFICE_PRESENTATION_FONT_SIZE_MAX_PT,
      }),
    ),
  ),
  fontFamily: Schema.optionalKey(OfficeFontFamilySchema),
  alignment: Schema.optionalKey(OfficePresentationTextAlignmentSchema),
}).check(
  Schema.makeFilter(
    (format) =>
      format.bold !== undefined ||
      format.italic !== undefined ||
      format.underline !== undefined ||
      format.fontSizePt !== undefined ||
      format.fontFamily !== undefined ||
      format.alignment !== undefined ||
      "a presentation text format patch must change at least one supported property",
  ),
);
export const OfficePresentationTransitionKindSchema = Schema.Literals([
  "none",
  "morph",
  "fade",
  "push",
  "wipe",
  "split",
  "circle",
  "cover",
  "pull",
  "dissolve",
  "zoom",
  "random",
]);
export const OfficePresentationAnimationEffectSchema = Schema.Literals([
  "appear",
  "fade",
  "flyIn",
  "wipe",
  "wipeDown",
  "splitIn",
  "bounce",
  "flipIn",
  "zoom",
  "pulse",
  "spin",
  "grow",
  "teeter",
  "disappear",
  "fadeOut",
  "flyOut",
  "wipeOut",
  "shrink",
  "zoomOut",
  "motionPath",
]);
export const OfficePresentationAnimationTriggerSchema = Schema.Literals([
  "onClick",
  "withPrev",
  "afterPrev",
]);
const PresentationAnimationTimeSchema = Schema.Int.check(
  Schema.isBetween({ minimum: 0, maximum: OFFICE_PRESENTATION_ANIMATION_TIME_MAX_MS }),
);
const PresentationAnimationParagraphSchema = Schema.Int.check(
  Schema.isBetween({ minimum: 0, maximum: 9_999 }),
);
const PresentationMotionPathTokenPattern =
  /[MLCZmlcz]|[-+]?(?:(?:\d+(?:\.\d*)?)|(?:\.\d+))(?:[eE][-+]?\d+)?/gu;

function presentationMotionPathIssue(value: string): true | string {
  if (value !== value.trim()) return "must be trimmed";
  const tokens = value.match(PresentationMotionPathTokenPattern);
  if (
    tokens === null ||
    value.replace(PresentationMotionPathTokenPattern, "").replaceAll(/[\s,]/gu, "") !== ""
  ) {
    return "must contain only M, L, C, Z, separators, and finite numeric coordinates";
  }
  let index = 0;
  let command = "";
  while (index < tokens.length) {
    const token = tokens[index] ?? "";
    if (/^[MLCZ]$/iu.test(token)) {
      command = token;
      index += 1;
      if (command.toUpperCase() === "Z") {
        command = "";
        continue;
      }
    }
    if (command === "") return "must begin each coordinate sequence with M, L, or C";
    if (index === 1 && command.toUpperCase() !== "M") return "must begin with an M command";
    const coordinateCount = command.toUpperCase() === "C" ? 6 : 2;
    const coordinates = tokens.slice(index, index + coordinateCount);
    if (
      coordinates.length !== coordinateCount ||
      coordinates.some((coordinate) => /^[MLCZ]$/iu.test(coordinate))
    ) {
      return `${command.toUpperCase()} must have ${coordinateCount} numeric coordinates`;
    }
    if (coordinates.some((coordinate) => !Number.isFinite(Number(coordinate)))) {
      return "must contain finite numeric coordinates";
    }
    index += coordinateCount;
    if (command.toUpperCase() === "M") command = command === "m" ? "l" : "L";
  }
  return true;
}

export const OfficePresentationMotionPathSchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(OFFICE_PRESENTATION_MOTION_PATH_MAX_CHARACTERS),
  Schema.makeFilter(presentationMotionPathIssue),
);
const PresentationAnimationBaseFields = {
  elementId: CanonicalIdSchema,
  trigger: OfficePresentationAnimationTriggerSchema,
  durationMs: PresentationAnimationTimeSchema,
  delayMs: PresentationAnimationTimeSchema,
  paragraph: Schema.optionalKey(PresentationAnimationParagraphSchema),
};
export const OfficePresentationAnimationSchema = Schema.Union([
  Schema.Struct({
    ...PresentationAnimationBaseFields,
    effect: Schema.Literal("motionPath"),
    motionPath: OfficePresentationMotionPathSchema,
  }),
  Schema.Struct({
    ...PresentationAnimationBaseFields,
    effect: Schema.Literals([
      "appear",
      "fade",
      "flyIn",
      "wipe",
      "wipeDown",
      "splitIn",
      "bounce",
      "flipIn",
      "zoom",
      "pulse",
      "spin",
      "grow",
      "teeter",
      "disappear",
      "fadeOut",
      "flyOut",
      "wipeOut",
      "shrink",
      "zoomOut",
    ]),
  }),
]);
export const OfficePresentationAnimationListSchema = Schema.Array(
  OfficePresentationAnimationSchema,
).check(Schema.isMaxLength(OFFICE_PRESENTATION_ANIMATION_MAX_ITEMS));
export const isOfficePresentationAnimation = Schema.is(OfficePresentationAnimationSchema);
export const OfficePresentationSlideTransitionSchema = Schema.Struct({
  slideId: CanonicalIdSchema,
  transition: OfficePresentationTransitionKindSchema,
});
export const OfficePresentationSlideTransitionListSchema = Schema.Array(
  OfficePresentationSlideTransitionSchema,
).check(Schema.isMinLength(1), Schema.isMaxLength(OFFICE_PRESENTATION_DECK_TRANSITION_MAX_SLIDES));

function decodedCanonicalBase64Length(value: string): number {
  const padding = value.endsWith("==") ? 2 : value.endsWith("=") ? 1 : 0;
  return (value.length / 4) * 3 - padding;
}

const OfficePresentationPictureMediaTypeSchema = Schema.Literals([
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "image/bmp",
]);
const OfficePresentationPictureByteSizeSchema = Schema.Int.check(
  Schema.isBetween({ minimum: 1, maximum: OFFICE_PRESENTATION_PICTURE_MAX_BYTES }),
);
const OfficePresentationPictureBase64Schema = Schema.String.check(
  Schema.isMinLength(4),
  Schema.isMaxLength(Math.ceil(OFFICE_PRESENTATION_PICTURE_MAX_BYTES / 3) * 4),
  Schema.makeFilter(
    (value) =>
      /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/u.test(value) ||
      "must be canonical base64",
  ),
);
const PresentationPicturePayloadFields = {
  mediaType: OfficePresentationPictureMediaTypeSchema,
  byteSize: OfficePresentationPictureByteSizeSchema,
  bytesBase64: OfficePresentationPictureBase64Schema,
} as const;
function presentationPicturePayloadIssue(payload: {
  readonly byteSize: number;
  readonly bytesBase64: string;
}): true | string {
  return (
    decodedCanonicalBase64Length(payload.bytesBase64) === payload.byteSize ||
    "picture byteSize must match its base64 payload"
  );
}
const PresentationPictureCropSchema = Schema.Struct({
  l: Schema.Finite.check(Schema.isBetween({ minimum: 0, maximum: 1 })),
  t: Schema.Finite.check(Schema.isBetween({ minimum: 0, maximum: 1 })),
  r: Schema.Finite.check(Schema.isBetween({ minimum: 0, maximum: 1 })),
  b: Schema.Finite.check(Schema.isBetween({ minimum: 0, maximum: 1 })),
}).check(
  Schema.makeFilter(
    (crop) =>
      (crop.l + crop.r < 1 && crop.t + crop.b < 1) ||
      "picture crop insets must leave a positive remaining rectangle",
  ),
);
export const OfficePresentationPresetShapeKindSchema = Schema.Literals(
  OfficePresentationPresetShapeKinds,
);
const PresentationTableDimensionSchema = Schema.Int.check(
  Schema.isBetween({ minimum: 1, maximum: OFFICE_PRESENTATION_TABLE_DIMENSION_MAXIMUM }),
);
const PresentationTableIndexSchema = Schema.Int.check(
  Schema.isBetween({ minimum: 0, maximum: OFFICE_PRESENTATION_TABLE_DIMENSION_MAXIMUM - 1 }),
);
const PresentationPictureInsertOperationSchema = Schema.Struct({
  type: Schema.Literal("presentation.element.insert-picture"),
  slideId: CanonicalIdSchema,
  elementId: CanonicalIdSchema,
  ...PresentationPicturePayloadFields,
  transform: SlideTransformSchema,
}).check(Schema.makeFilter(presentationPicturePayloadIssue));
const PresentationPictureChangeOperationSchema = Schema.Struct({
  type: Schema.Literal("presentation.element.change-picture"),
  slideId: CanonicalIdSchema,
  elementId: CanonicalIdSchema,
  ...PresentationPicturePayloadFields,
}).check(Schema.makeFilter(presentationPicturePayloadIssue));
const PresentationPictureCropOperationSchema = Schema.Struct({
  type: Schema.Literal("presentation.element.set-picture-crop"),
  slideId: CanonicalIdSchema,
  elementId: CanonicalIdSchema,
  crop: Schema.Union([PresentationPictureCropSchema, Schema.Null]),
});
const PresentationShapeInsertOperationSchema = Schema.Struct({
  type: Schema.Literal("presentation.element.insert-shape"),
  slideId: CanonicalIdSchema,
  elementId: CanonicalIdSchema,
  preset: OfficePresentationPresetShapeKindSchema,
  transform: SlideTransformSchema,
});
const PresentationTableInsertOperationSchema = Schema.Struct({
  type: Schema.Literal("presentation.element.insert-table"),
  slideId: CanonicalIdSchema,
  elementId: CanonicalIdSchema,
  rowCount: PresentationTableDimensionSchema,
  columnCount: PresentationTableDimensionSchema,
  transform: SlideTransformSchema,
});
const PresentationTableRowOperationSchema = Schema.Struct({
  type: Schema.Literals(["presentation.table.insert-row", "presentation.table.delete-row"]),
  slideId: CanonicalIdSchema,
  elementId: CanonicalIdSchema,
  index: PresentationTableIndexSchema,
  before: Schema.optionalKey(Schema.Boolean),
});
const PresentationTableColumnOperationSchema = Schema.Struct({
  type: Schema.Literals([
    "presentation.table.insert-column",
    "presentation.table.delete-column",
  ]),
  slideId: CanonicalIdSchema,
  elementId: CanonicalIdSchema,
  index: PresentationTableIndexSchema,
  before: Schema.optionalKey(Schema.Boolean),
});

const PresentationOperationSchema = Schema.Union([
  Schema.Struct({
    type: Schema.Literal("presentation.deck.transition.set"),
    transition: OfficePresentationTransitionKindSchema,
  }),
  Schema.Struct({
    type: Schema.Literal("presentation.deck.transitions.set"),
    transitions: OfficePresentationSlideTransitionListSchema,
  }),
  Schema.Struct({
    type: Schema.Literal("presentation.slide.add"),
    slideId: CanonicalIdSchema,
    afterSlideId: Schema.optionalKey(CanonicalIdSchema),
    layoutId: Schema.optionalKey(CanonicalIdSchema),
  }),
  Schema.Struct({ type: Schema.Literal("presentation.slide.delete"), slideId: CanonicalIdSchema }),
  Schema.Struct({
    type: Schema.Literal("presentation.slide.duplicate"),
    sourceSlideId: CanonicalIdSchema,
    slideId: CanonicalIdSchema,
  }),
  Schema.Struct({
    type: Schema.Literal("presentation.slide.move"),
    slideId: CanonicalIdSchema,
    afterSlideId: Schema.optionalKey(CanonicalIdSchema),
  }),
  Schema.Struct({
    type: Schema.Literal("presentation.slide.visibility.set"),
    slideId: CanonicalIdSchema,
    hidden: Schema.Boolean,
  }),
  Schema.Struct({
    type: Schema.Literal("presentation.slide.transition.set"),
    slideId: CanonicalIdSchema,
    transition: OfficePresentationTransitionKindSchema,
  }),
  Schema.Struct({
    type: Schema.Literal("presentation.slide.animations.set"),
    slideId: CanonicalIdSchema,
    animations: OfficePresentationAnimationListSchema,
  }),
  Schema.Struct({
    type: Schema.Literal("presentation.element.set-text"),
    slideId: CanonicalIdSchema,
    elementId: CanonicalIdSchema,
    text: RichTextSchema,
  }),
  Schema.Struct({
    type: Schema.Literal("presentation.element.insert-text"),
    slideId: CanonicalIdSchema,
    elementId: CanonicalIdSchema,
    text: PresentationTextBoxTextSchema,
    transform: SlideTransformSchema,
  }),
  PresentationPictureInsertOperationSchema,
  PresentationPictureChangeOperationSchema,
  PresentationPictureCropOperationSchema,
  PresentationShapeInsertOperationSchema,
  PresentationTableInsertOperationSchema,
  PresentationTableRowOperationSchema,
  PresentationTableColumnOperationSchema,
  Schema.Struct({
    type: Schema.Literal("presentation.element.set-transform"),
    slideId: CanonicalIdSchema,
    elementId: CanonicalIdSchema,
    transform: SlideTransformSchema,
  }),
  Schema.Struct({
    type: Schema.Literal("presentation.element.set-text-format"),
    slideId: CanonicalIdSchema,
    elementId: CanonicalIdSchema,
    format: OfficePresentationTextFormatPatchSchema,
  }),
  Schema.Struct({
    type: Schema.Literal("presentation.element.delete"),
    slideId: CanonicalIdSchema,
    elementId: CanonicalIdSchema,
  }),
  Schema.Struct({
    type: Schema.Literal("presentation.speaker-notes.set"),
    slideId: CanonicalIdSchema,
    text: RichTextSchema,
  }),
]);

const SpreadsheetScalarSchema = Schema.Union([
  Schema.String.check(Schema.isMaxLength(32_000)),
  Schema.Finite,
  Schema.Boolean,
  Schema.Null,
]);
const SpreadsheetCellInputSchema = Schema.Union([
  Schema.Struct({ type: Schema.Literal("value"), value: SpreadsheetScalarSchema }),
  Schema.Struct({
    type: Schema.Literal("formula"),
    formula: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(8_192)),
    cachedValue: Schema.optionalKey(SpreadsheetScalarSchema),
  }),
]);
export const OfficeSpreadsheetHorizontalAlignmentSchema = Schema.Literals([
  "left",
  "center",
  "right",
  "fill",
  "justify",
  "centerContinuous",
  "distributed",
]);
export const OfficeSpreadsheetNumberFormatSchema = Schema.Literals([
  "general",
  "number",
  "currency",
  "percent",
  "date",
]);
export const OfficeSpreadsheetSheetNameSchema = CanonicalTextSchema.check(
  Schema.isMaxLength(31),
  Schema.makeFilter((name) => !/[\\/?*[\]:]/u.test(name), {
    expected: "a worksheet name without \\ / ? * [ ] or :",
  }),
  Schema.makeFilter((name) => !name.startsWith("'") && !name.endsWith("'"), {
    expected: "a worksheet name that does not start or end with an apostrophe",
  }),
);
export const isOfficeSpreadsheetSheetName = Schema.is(OfficeSpreadsheetSheetNameSchema);
function spreadsheetCellAddressCoordinates(
  value: string,
): readonly [row: number, column: number] | undefined {
  const match = /^([A-Z]{1,3})([1-9][0-9]{0,6})$/u.exec(value);
  if (!match?.[1] || !match[2]) return undefined;
  let column = 0;
  for (const character of match[1]) column = column * 26 + character.charCodeAt(0) - 64;
  const row = Number(match[2]);
  return column <= 16_384 && row <= 1_048_576 ? [row, column] : undefined;
}

function spreadsheetCellAddressIsWithinBounds(value: string): boolean {
  return spreadsheetCellAddressCoordinates(value) !== undefined;
}

export const OfficeSpreadsheetCellAddressSchema = CanonicalTextSchema.check(
  Schema.isMaxLength(10),
  Schema.makeFilter(
    (value) =>
      spreadsheetCellAddressIsWithinBounds(value) ||
      "an uppercase A1 cell address within XLSX bounds",
  ),
);

function spreadsheetCellRangeIsCanonical(value: string): boolean {
  const parts = value.split(":");
  if (parts.length > 2 || !parts[0]) return false;
  const start = spreadsheetCellAddressCoordinates(parts[0]);
  if (!start) return false;
  if (!parts[1]) return parts.length === 1;
  const end = spreadsheetCellAddressCoordinates(parts[1]);
  if (!end) return false;
  const [startRow, startColumn] = start;
  const [endRow, endColumn] = end;
  return startRow <= endRow && startColumn <= endColumn;
}

export const OfficeSpreadsheetCellRangeSchema = CanonicalTextSchema.check(
  Schema.isMaxLength(21),
  Schema.makeFilter(
    (value) =>
      spreadsheetCellRangeIsCanonical(value) ||
      "an uppercase A1 range ordered from its top-left to bottom-right cell",
  ),
);

function spreadsheetTableRangeIssue(value: string): true | string {
  const [startAddress, endAddress, extra] = value.split(":");
  const start = startAddress ? spreadsheetCellAddressCoordinates(startAddress) : undefined;
  const end = endAddress ? spreadsheetCellAddressCoordinates(endAddress) : undefined;
  return (
    (extra === undefined && start !== undefined && end !== undefined && end[0] > start[0]) ||
    "an XLSX range with one header row and at least one data row"
  );
}

export const OfficeSpreadsheetTableRangeSchema = OfficeSpreadsheetCellRangeSchema.check(
  Schema.makeFilter(spreadsheetTableRangeIssue),
);

function spreadsheetChartRangeIssue(value: string): true | string {
  const [startAddress, endAddress] = value.split(":");
  const start = startAddress ? spreadsheetCellAddressCoordinates(startAddress) : undefined;
  const end = endAddress ? spreadsheetCellAddressCoordinates(endAddress) : undefined;
  if (!start || !end) return "a bounded rectangular XLSX chart data range";
  const [startRow, startColumn] = start;
  const [endRow, endColumn] = end;
  const cellCount = (endRow - startRow + 1) * (endColumn - startColumn + 1);
  return (
    (endRow > startRow && endColumn > startColumn && cellCount <= 2_000) ||
    "an XLSX chart data range with headers, categories, values, and at most 2,000 cells"
  );
}

export const OfficeSpreadsheetChartRangeSchema = OfficeSpreadsheetCellRangeSchema.check(
  Schema.makeFilter(spreadsheetChartRangeIssue),
);
export const OfficeSpreadsheetChartTypeSchema = Schema.Literals(["column", "bar", "line", "pie"]);

function spreadsheetDataValidationRangeIssue(value: string): true | string {
  const [startAddress, endAddress] = value.split(":");
  const start = startAddress ? spreadsheetCellAddressCoordinates(startAddress) : undefined;
  const end = endAddress ? spreadsheetCellAddressCoordinates(endAddress) : start;
  if (!start || !end) return "a bounded XLSX data-validation range";
  const cellCount = (end[0] - start[0] + 1) * (end[1] - start[1] + 1);
  return (
    cellCount <= OFFICE_SPREADSHEET_DATA_VALIDATION_MAX_CELLS ||
    `an XLSX data-validation range with at most ${OFFICE_SPREADSHEET_DATA_VALIDATION_MAX_CELLS} cells`
  );
}

export const OfficeSpreadsheetDataValidationRangeSchema = OfficeSpreadsheetCellRangeSchema.check(
  Schema.makeFilter(spreadsheetDataValidationRangeIssue),
);
export const isOfficeSpreadsheetDataValidationRange = Schema.is(
  OfficeSpreadsheetDataValidationRangeSchema,
);
const OfficeSpreadsheetDataValidationListValueSchema = CanonicalTextSchema.check(
  Schema.isMaxLength(OFFICE_SPREADSHEET_DATA_VALIDATION_LIST_VALUE_MAX_CHARACTERS),
  Schema.makeFilter(
    (value) =>
      (!value.includes(",") && !value.includes('"')) ||
      "a data-validation list value without commas or quotes",
  ),
  Schema.makeFilter(
    (value) =>
      Array.from(value).every((character) => {
        const codePoint = character.codePointAt(0);
        return (
          codePoint !== undefined &&
          codePoint !== 0xfffe &&
          codePoint !== 0xffff &&
          !(codePoint >= 0xd800 && codePoint <= 0xdfff)
        );
      }) || "an XML-compatible data-validation list value",
  ),
);

function spreadsheetDataValidationListIssue(values: readonly string[]): true | string {
  const normalized = values.map((value) => value.toLocaleLowerCase("en-US"));
  if (new Set(normalized).size !== values.length) {
    return "a data-validation list with unique case-insensitive values";
  }
  return (
    values.join(",").length + 2 <= OFFICE_SPREADSHEET_DATA_VALIDATION_LIST_FORMULA_MAX_CHARACTERS ||
    `a data-validation list within the ${OFFICE_SPREADSHEET_DATA_VALIDATION_LIST_FORMULA_MAX_CHARACTERS}-character native formula limit`
  );
}

export const OfficeSpreadsheetDataValidationListValuesSchema = Schema.Array(
  OfficeSpreadsheetDataValidationListValueSchema,
).check(
  Schema.isMinLength(1),
  Schema.isMaxLength(OFFICE_SPREADSHEET_DATA_VALIDATION_LIST_MAX_VALUES),
  Schema.makeFilter(spreadsheetDataValidationListIssue),
);
export const isOfficeSpreadsheetDataValidationListValues = Schema.is(
  OfficeSpreadsheetDataValidationListValuesSchema,
);

function spreadsheetSparklineRangeIssue(value: string): true | string {
  const [startAddress, endAddress, extra] = value.split(":");
  const start = startAddress ? spreadsheetCellAddressCoordinates(startAddress) : undefined;
  const end = endAddress ? spreadsheetCellAddressCoordinates(endAddress) : undefined;
  if (extra !== undefined || !start || !end) {
    return "a bounded rectangular XLSX sparkline source range";
  }
  const rowCount = end[0] - start[0] + 1;
  const columnCount = end[1] - start[1] + 1;
  return (
    (rowCount >= 1 &&
      rowCount <= OFFICE_SPREADSHEET_SPARKLINE_MAX_ROWS &&
      columnCount >= 2 &&
      columnCount <= OFFICE_SPREADSHEET_SPARKLINE_MAX_COLUMNS &&
      end[1] < 16_384) ||
    `an XLSX sparkline source with 1-${OFFICE_SPREADSHEET_SPARKLINE_MAX_ROWS} rows, 2-${OFFICE_SPREADSHEET_SPARKLINE_MAX_COLUMNS} columns, and one adjacent host column`
  );
}

export const OfficeSpreadsheetSparklineRangeSchema = OfficeSpreadsheetCellRangeSchema.check(
  Schema.makeFilter(spreadsheetSparklineRangeIssue),
);
export const isOfficeSpreadsheetSparklineRange = Schema.is(OfficeSpreadsheetSparklineRangeSchema);
export const OfficeSpreadsheetSparklineTypeSchema = Schema.Literals(["line", "column", "win-loss"]);

function spreadsheetConditionalFormatRangeIssue(value: string): true | string {
  const [startAddress, endAddress, extra] = value.split(":");
  const start = startAddress ? spreadsheetCellAddressCoordinates(startAddress) : undefined;
  const end = endAddress ? spreadsheetCellAddressCoordinates(endAddress) : undefined;
  if (extra !== undefined || !start || !end) {
    return "a bounded rectangular XLSX conditional-formatting range";
  }
  const cellCount = (end[0] - start[0] + 1) * (end[1] - start[1] + 1);
  return (
    (cellCount >= 2 && cellCount <= OFFICE_SPREADSHEET_CONDITIONAL_FORMAT_MAX_CELLS) ||
    `an XLSX conditional-formatting range containing 2-${OFFICE_SPREADSHEET_CONDITIONAL_FORMAT_MAX_CELLS} cells`
  );
}

export const OfficeSpreadsheetConditionalFormatRangeSchema = OfficeSpreadsheetCellRangeSchema.check(
  Schema.makeFilter(spreadsheetConditionalFormatRangeIssue),
);
export const isOfficeSpreadsheetConditionalFormatRange = Schema.is(
  OfficeSpreadsheetConditionalFormatRangeSchema,
);

function spreadsheetDefinedNameIssue(name: string): true | string {
  const lower = name.toLocaleLowerCase("en-US");
  return (
    (/^[\p{L}_\\][\p{L}\p{N}_.]*$/u.test(name) &&
      !spreadsheetCellAddressIsWithinBounds(name.toUpperCase()) &&
      !/^(?:[Rr]|[Cc]|[Rr][0-9]*[Cc][0-9]*)$/u.test(name) &&
      lower !== "true" &&
      lower !== "false" &&
      !lower.startsWith("_xlnm")) ||
    "a non-reserved Excel defined name that is not a cell reference"
  );
}

export const OfficeSpreadsheetDefinedNameSchema = CanonicalTextSchema.check(
  Schema.isMaxLength(255),
  Schema.makeFilter(spreadsheetDefinedNameIssue),
);
export const isOfficeSpreadsheetDefinedName = Schema.is(OfficeSpreadsheetDefinedNameSchema);

const decodeSpreadsheetHyperlinkUrl = Schema.decodeUnknownOption(Schema.URLFromString);

function spreadsheetExternalHyperlinkTargetIssue(value: string): true | string {
  const escapedLength = value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&apos;").length;
  if (escapedLength > OFFICE_SPREADSHEET_HYPERLINK_TARGET_MAX_CHARACTERS) {
    return "a hyperlink that fits the native OOXML target bound after encoding";
  }
  const decoded = decodeSpreadsheetHyperlinkUrl(value);
  if (Option.isNone(decoded)) return "an absolute HTTP(S) or mailto hyperlink";
  const url = decoded.value;
  if (url.protocol === "mailto:") {
    return url.pathname.length > 0 || "a mailto hyperlink with a recipient";
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") {
    return "an HTTP(S) or mailto hyperlink";
  }
  return (
    (url.hostname !== "" && url.username === "" && url.password === "") ||
    "an HTTP(S) hyperlink without userinfo"
  );
}

export const OfficeSpreadsheetHyperlinkTargetSchema = Schema.Union([
  Schema.Struct({
    kind: Schema.Literal("external"),
    url: CanonicalTextSchema.check(
      Schema.isMaxLength(OFFICE_SPREADSHEET_HYPERLINK_TARGET_MAX_CHARACTERS),
      Schema.makeFilter(spreadsheetExternalHyperlinkTargetIssue),
    ),
  }),
  Schema.Struct({
    kind: Schema.Literal("cell"),
    sheetId: CanonicalIdSchema,
    address: OfficeSpreadsheetCellAddressSchema,
  }),
]);
export const isOfficeSpreadsheetHyperlinkTarget = Schema.is(OfficeSpreadsheetHyperlinkTargetSchema);
export const OfficeSpreadsheetCellFormatPatchSchema = Schema.Struct({
  bold: Schema.optionalKey(Schema.Boolean),
  italic: Schema.optionalKey(Schema.Boolean),
  horizontalAlignment: Schema.optionalKey(
    Schema.Union([OfficeSpreadsheetHorizontalAlignmentSchema, Schema.Null]),
  ),
  numberFormat: Schema.optionalKey(OfficeSpreadsheetNumberFormatSchema),
}).check(
  Schema.makeFilter(
    (format) =>
      format.bold !== undefined ||
      format.italic !== undefined ||
      format.horizontalAlignment !== undefined ||
      format.numberFormat !== undefined ||
      "a cell format patch must change at least one supported property",
  ),
);
const SpreadsheetCellFormatRowsSchema = Schema.Array(
  Schema.Array(OfficeSpreadsheetCellFormatPatchSchema).check(
    Schema.isMinLength(1),
    Schema.isMaxLength(256),
  ),
)
  .check(Schema.isMinLength(1), Schema.isMaxLength(256))
  .check(
    Schema.makeFilter(
      (rows) =>
        rows.every((row) => row.length === rows[0]?.length) ||
        "spreadsheet format rows must form one rectangular range",
    ),
  );
const SpreadsheetFreezeCountSchema = Schema.Int.check(
  Schema.isBetween({ minimum: 0, maximum: OFFICE_SPREADSHEET_FREEZE_COUNT_MAXIMUM }),
);
const SpreadsheetOperationSchema = Schema.Union([
  Schema.Struct({
    type: Schema.Literal("spreadsheet.range.set"),
    sheetId: CanonicalIdSchema,
    startCell: OfficeSpreadsheetCellAddressSchema,
    rows: Schema.Array(
      Schema.Array(SpreadsheetCellInputSchema).check(
        Schema.isMinLength(1),
        Schema.isMaxLength(256),
      ),
    ).check(Schema.isMinLength(1), Schema.isMaxLength(256)),
  }),
  Schema.Struct({
    type: Schema.Literal("spreadsheet.range.format"),
    sheetId: CanonicalIdSchema,
    startCell: OfficeSpreadsheetCellAddressSchema,
    rows: SpreadsheetCellFormatRowsSchema,
  }),
  Schema.Struct({
    type: Schema.Literal("spreadsheet.sheet.add"),
    sheetId: CanonicalIdSchema,
    name: OfficeSpreadsheetSheetNameSchema,
  }),
  Schema.Struct({
    type: Schema.Literal("spreadsheet.sheet.rename"),
    sheetId: CanonicalIdSchema,
    name: OfficeSpreadsheetSheetNameSchema,
  }),
  Schema.Struct({
    type: Schema.Literal("spreadsheet.sheet.duplicate"),
    sourceSheetId: CanonicalIdSchema,
    sheetId: CanonicalIdSchema,
    name: OfficeSpreadsheetSheetNameSchema,
  }),
  Schema.Struct({ type: Schema.Literal("spreadsheet.sheet.delete"), sheetId: CanonicalIdSchema }),
  Schema.Struct({
    type: Schema.Literal("spreadsheet.sheet.visibility.set"),
    sheetId: CanonicalIdSchema,
    hidden: Schema.Boolean,
  }),
  Schema.Struct({
    type: Schema.Literal("spreadsheet.sheet.reorder"),
    sheetId: CanonicalIdSchema,
    /** Zero-based target position in the final worksheet tab order. */
    targetIndex: NonNegativeIntegerSchema,
  }),
  Schema.Struct({
    type: Schema.Literal("spreadsheet.freeze.set"),
    sheetId: CanonicalIdSchema,
    /** Number of rows frozen from the top; `0` clears the row freeze. */
    rows: SpreadsheetFreezeCountSchema,
    /** Number of columns frozen from the left; `0` clears the column freeze. */
    columns: SpreadsheetFreezeCountSchema,
  }),
  Schema.Struct({
    type: Schema.Literal("spreadsheet.hyperlink.set"),
    sheetId: CanonicalIdSchema,
    address: OfficeSpreadsheetCellAddressSchema,
    target: Schema.Union([OfficeSpreadsheetHyperlinkTargetSchema, Schema.Null]),
  }),
  Schema.Struct({
    type: Schema.Literal("spreadsheet.named-range.add"),
    name: OfficeSpreadsheetDefinedNameSchema,
    sheetId: CanonicalIdSchema,
    range: OfficeSpreadsheetCellRangeSchema,
  }),
  Schema.Struct({
    type: Schema.Literal("spreadsheet.table.add"),
    name: OfficeSpreadsheetDefinedNameSchema,
    sheetId: CanonicalIdSchema,
    range: OfficeSpreadsheetTableRangeSchema,
  }),
  Schema.Struct({
    type: Schema.Literal("spreadsheet.chart.add"),
    chartId: CanonicalIdSchema,
    sheetId: CanonicalIdSchema,
    range: OfficeSpreadsheetChartRangeSchema,
    chartType: OfficeSpreadsheetChartTypeSchema,
    title: CanonicalTextSchema.check(Schema.isMaxLength(255)),
  }),
  Schema.Struct({
    type: Schema.Literal("spreadsheet.data-validation.list.add"),
    sheetId: CanonicalIdSchema,
    range: OfficeSpreadsheetDataValidationRangeSchema,
    values: OfficeSpreadsheetDataValidationListValuesSchema,
    allowBlank: Schema.Boolean,
  }),
  Schema.Struct({
    type: Schema.Literal("spreadsheet.sparkline.add"),
    sheetId: CanonicalIdSchema,
    range: OfficeSpreadsheetSparklineRangeSchema,
    sparklineType: OfficeSpreadsheetSparklineTypeSchema,
  }),
  Schema.Struct({
    type: Schema.Literal("spreadsheet.conditional-format.color-scale.add"),
    sheetId: CanonicalIdSchema,
    range: OfficeSpreadsheetConditionalFormatRangeSchema,
  }),
  Schema.Struct({
    type: Schema.Literal("spreadsheet.conditional-format.data-bar.add"),
    sheetId: CanonicalIdSchema,
    range: OfficeSpreadsheetConditionalFormatRangeSchema,
  }),
  Schema.Struct({
    type: Schema.Literal("spreadsheet.conditional-format.icon-set.add"),
    sheetId: CanonicalIdSchema,
    range: OfficeSpreadsheetConditionalFormatRangeSchema,
  }),
  Schema.Struct({
    type: Schema.Literal("spreadsheet.range.fill"),
    sheetId: CanonicalIdSchema,
    source: CanonicalTextSchema.check(Schema.isMaxLength(64)),
    target: CanonicalTextSchema.check(Schema.isMaxLength(64)),
  }),
  Schema.Struct({
    type: Schema.Literal("spreadsheet.range.sort"),
    sheetId: CanonicalIdSchema,
    range: CanonicalTextSchema.check(Schema.isMaxLength(64)),
    /** Zero-based sort-key offset from the first column in `range`. */
    columnIndex: NonNegativeIntegerSchema,
    direction: Schema.Literals(["ascending", "descending"]),
    hasHeader: Schema.Boolean,
  }),
  Schema.Struct({
    type: Schema.Literal("spreadsheet.filter.set"),
    sheetId: CanonicalIdSchema,
    range: CanonicalTextSchema.check(Schema.isMaxLength(64)),
  }),
  Schema.Struct({
    type: Schema.Literal("spreadsheet.filter.clear"),
    sheetId: CanonicalIdSchema,
  }),
  Schema.Struct({
    type: Schema.Literal("spreadsheet.rows.insert"),
    sheetId: CanonicalIdSchema,
    index: NonNegativeIntegerSchema,
    count: Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 256 })),
  }),
  Schema.Struct({
    type: Schema.Literal("spreadsheet.rows.remove"),
    sheetId: CanonicalIdSchema,
    index: NonNegativeIntegerSchema,
    count: Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 256 })),
  }),
  Schema.Struct({
    type: Schema.Literal("spreadsheet.columns.insert"),
    sheetId: CanonicalIdSchema,
    index: NonNegativeIntegerSchema,
    count: Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 256 })),
  }),
  Schema.Struct({
    type: Schema.Literal("spreadsheet.columns.remove"),
    sheetId: CanonicalIdSchema,
    index: NonNegativeIntegerSchema,
    count: Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 256 })),
  }),
]);

const OfficePdfHighlightCoordinateSchema = Schema.Finite.check(
  Schema.isBetween({
    minimum: -OFFICE_PDF_HIGHLIGHT_COORDINATE_LIMIT_PT,
    maximum: OFFICE_PDF_HIGHLIGHT_COORDINATE_LIMIT_PT,
  }),
);
const OfficePdfNormalizedColorComponentSchema = Schema.Finite.check(
  Schema.isBetween({ minimum: 0, maximum: 1 }),
);
export const OfficePdfHighlightRectSchema = Schema.Struct({
  left: OfficePdfHighlightCoordinateSchema,
  bottom: OfficePdfHighlightCoordinateSchema,
  right: OfficePdfHighlightCoordinateSchema,
  top: OfficePdfHighlightCoordinateSchema,
}).check(
  Schema.makeFilter(
    (rect) =>
      (rect.right > rect.left && rect.top > rect.bottom) ||
      "PDF highlight rectangles must have positive normalized extents",
  ),
);
export const OfficePdfHighlightColorSchema = Schema.Struct({
  red: OfficePdfNormalizedColorComponentSchema,
  green: OfficePdfNormalizedColorComponentSchema,
  blue: OfficePdfNormalizedColorComponentSchema,
});
const OfficePdfNoteContentsSchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(OFFICE_PDF_NOTE_CONTENTS_MAX_CHARACTERS),
  Schema.makeFilter(
    (value) =>
      Array.from(value).every((character) => {
        const codePoint = character.codePointAt(0);
        return (
          codePoint !== undefined &&
          (codePoint === 9 ||
            codePoint === 10 ||
            codePoint === 13 ||
            (codePoint > 31 && codePoint !== 127))
        );
      }) || "PDF note contents must be text without control characters",
  ),
);
const PdfOperationSchema = Schema.Union([
  Schema.Struct({ type: Schema.Literal("pdf.page.delete"), pageId: OfficePdfPageIdSchema }),
  Schema.Struct({
    type: Schema.Literal("pdf.page.move"),
    pageId: OfficePdfPageIdSchema,
    afterPageId: Schema.optionalKey(OfficePdfPageIdSchema),
  }),
  Schema.Struct({
    type: Schema.Literal("pdf.page.rotate"),
    pageId: OfficePdfPageIdSchema,
    delta: Schema.Literals([-90, 90]),
  }),
  Schema.Struct({
    type: Schema.Literal("pdf.annotation.highlight.add"),
    pageId: OfficePdfPageIdSchema,
    annotationId: CanonicalIdSchema,
    coordinateSpace: Schema.Literal("pdf-user-space"),
    rects: Schema.Array(OfficePdfHighlightRectSchema).check(
      Schema.isMinLength(1),
      Schema.isMaxLength(OFFICE_PDF_HIGHLIGHT_MAX_RECTS),
    ),
    color: OfficePdfHighlightColorSchema,
    opacity: Schema.Finite.check(
      Schema.isBetween({ minimum: OFFICE_PDF_HIGHLIGHT_OPACITY_MINIMUM, maximum: 1 }),
    ),
  }),
  Schema.Struct({
    type: Schema.Literal("pdf.annotation.underline.add"),
    pageId: OfficePdfPageIdSchema,
    annotationId: CanonicalIdSchema,
    coordinateSpace: Schema.Literal("pdf-user-space"),
    rects: Schema.Array(OfficePdfHighlightRectSchema).check(
      Schema.isMinLength(1),
      Schema.isMaxLength(OFFICE_PDF_HIGHLIGHT_MAX_RECTS),
    ),
    color: OfficePdfHighlightColorSchema,
    opacity: Schema.Finite.check(
      Schema.isBetween({ minimum: OFFICE_PDF_HIGHLIGHT_OPACITY_MINIMUM, maximum: 1 }),
    ),
  }),
  Schema.Struct({
    type: Schema.Literal("pdf.annotation.strikeout.add"),
    pageId: OfficePdfPageIdSchema,
    annotationId: CanonicalIdSchema,
    coordinateSpace: Schema.Literal("pdf-user-space"),
    rects: Schema.Array(OfficePdfHighlightRectSchema).check(
      Schema.isMinLength(1),
      Schema.isMaxLength(OFFICE_PDF_HIGHLIGHT_MAX_RECTS),
    ),
    color: OfficePdfHighlightColorSchema,
    opacity: Schema.Finite.check(
      Schema.isBetween({ minimum: OFFICE_PDF_HIGHLIGHT_OPACITY_MINIMUM, maximum: 1 }),
    ),
  }),
  Schema.Struct({
    type: Schema.Literal("pdf.annotation.note.add"),
    pageId: OfficePdfPageIdSchema,
    annotationId: CanonicalIdSchema,
    coordinateSpace: Schema.Literal("pdf-user-space"),
    x: OfficePdfHighlightCoordinateSchema,
    y: OfficePdfHighlightCoordinateSchema,
    contents: OfficePdfNoteContentsSchema,
    color: OfficePdfHighlightColorSchema,
    author: Schema.optionalKey(CanonicalTextSchema.check(Schema.isMaxLength(160))),
  }),
  Schema.Struct({
    type: Schema.Literal("pdf.annotation.delete"),
    annotationId: CanonicalIdSchema,
  }),
  Schema.Struct({
    type: Schema.Literal("pdf.form.set-value"),
    fieldName: CanonicalTextSchema.check(
      Schema.isMaxLength(OFFICE_PDF_FORM_FIELD_NAME_MAX_CHARACTERS),
    ),
    value: Schema.Union([
      Schema.String.check(Schema.isMaxLength(OFFICE_PDF_FORM_FIELD_VALUE_MAX_CHARACTERS)),
      Schema.Boolean,
    ]),
  }),
]);

const MarkdownFrontmatterSchema = Schema.String.check(
  Schema.isMaxLength(OFFICE_MARKDOWN_FRONTMATTER_MAX_CHARACTERS),
).check(
  Schema.makeFilter((value) => {
    if (value === "") return true;
    if (!value.startsWith("---\n") && !value.startsWith("---\r\n")) {
      return "Markdown frontmatter must open with a YAML fence";
    }
    const rest = value.startsWith("---\r\n") ? value.slice(5) : value.slice(4);
    const closeLf = rest.split("\n").some((line) => line.replace(/\r$/u, "") === "---");
    return closeLf || "Markdown frontmatter must close with a YAML fence";
  }),
);
const MarkdownOperationSchema = Schema.Union([
  Schema.Struct({
    type: Schema.Literal("markdown.block.replace"),
    blockId: CanonicalIdSchema,
    markdown: RichTextSchema,
  }),
  Schema.Struct({
    type: Schema.Literal("markdown.block.insert"),
    blockId: CanonicalIdSchema,
    afterBlockId: Schema.optionalKey(CanonicalIdSchema),
    markdown: RichTextSchema,
  }),
  Schema.Struct({ type: Schema.Literal("markdown.block.delete"), blockId: CanonicalIdSchema }),
  Schema.Struct({
    type: Schema.Literal("markdown.frontmatter.set"),
    frontmatter: MarkdownFrontmatterSchema,
  }),
]);

const PatchEnvelopeFields = {
  schemaVersion: Schema.Literal(1),
  operationId: CanonicalIdSchema,
  requestId: CanonicalIdSchema,
  workspaceId: CanonicalIdSchema,
  projectId: CanonicalIdSchema,
  threadId: Schema.optionalKey(CanonicalIdSchema),
  artifactId: CanonicalIdSchema,
  baseRevisionId: CanonicalIdSchema,
  author: Schema.Union([
    Schema.Struct({ type: Schema.Literal("user"), id: CanonicalIdSchema }),
    Schema.Struct({
      type: Schema.Literal("agent"),
      id: CanonicalIdSchema,
      runId: CanonicalIdSchema,
    }),
  ]),
  summary: CanonicalTextSchema.check(Schema.isMaxLength(1_000)),
} as const;

export const OfficeDocumentPatchEnvelopeSchema = Schema.Struct({
  ...PatchEnvelopeFields,
  kind: Schema.Literal("document"),
  operations: Schema.Array(DocumentOperationSchema).check(
    Schema.isMinLength(1),
    Schema.isMaxLength(64),
  ),
});
export const OfficePresentationPatchEnvelopeSchema = Schema.Struct({
  ...PatchEnvelopeFields,
  kind: Schema.Literal("presentation"),
  operations: Schema.Array(PresentationOperationSchema).check(
    Schema.isMinLength(1),
    Schema.isMaxLength(64),
  ),
});
export const OfficeSpreadsheetPatchEnvelopeSchema = Schema.Struct({
  ...PatchEnvelopeFields,
  kind: Schema.Literal("spreadsheet"),
  operations: Schema.Array(SpreadsheetOperationSchema).check(
    Schema.isMinLength(1),
    Schema.isMaxLength(64),
  ),
});
export const OfficePdfPatchEnvelopeSchema = Schema.Struct({
  ...PatchEnvelopeFields,
  kind: Schema.Literal("pdf"),
  operations: Schema.Array(PdfOperationSchema).check(Schema.isMinLength(1), Schema.isMaxLength(64)),
});
export const OfficeMarkdownPatchEnvelopeSchema = Schema.Struct({
  ...PatchEnvelopeFields,
  kind: Schema.Literal("markdown"),
  operations: Schema.Array(MarkdownOperationSchema).check(
    Schema.isMinLength(1),
    Schema.isMaxLength(64),
  ),
});

export const OfficePatchableArtifactPatchEnvelopeSchema = Schema.Union([
  OfficeDocumentPatchEnvelopeSchema,
  OfficePresentationPatchEnvelopeSchema,
  OfficeSpreadsheetPatchEnvelopeSchema,
  OfficePdfPatchEnvelopeSchema,
  OfficeMarkdownPatchEnvelopeSchema,
]);

export const OfficeArtifactPatchEnvelopeSchema = Schema.Union([
  OfficeDocumentPatchEnvelopeSchema,
  OfficePresentationPatchEnvelopeSchema,
  OfficeSpreadsheetPatchEnvelopeSchema,
  OfficePdfPatchEnvelopeSchema,
  OfficeMarkdownPatchEnvelopeSchema,
]);

export const OfficeNativeIdentityAliasSchema = Schema.Struct({
  entity: Schema.Literals([
    "document-block",
    "document-comment",
    "markdown-block",
    "presentation-slide",
    "presentation-element",
    "spreadsheet-chart",
    "spreadsheet-sheet",
  ]),
  requestedId: CanonicalIdSchema,
  nativeId: CanonicalIdSchema,
});

const ImportRequestFields = {
  schemaVersion: Schema.Literal(1),
  requestId: CanonicalIdSchema,
  workspaceId: CanonicalIdSchema,
  projectId: CanonicalIdSchema,
  threadId: Schema.optionalKey(CanonicalIdSchema),
  sourceObjectId: CanonicalIdSchema,
  fileName: FileNameSchema,
  title: TitleSchema,
} as const;
export const OfficeImportRequestSchema = Schema.Union([
  Schema.Struct({
    ...ImportRequestFields,
    kind: Schema.Literal("document"),
    mediaType: Schema.Literal(
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ),
  }),
  Schema.Struct({
    ...ImportRequestFields,
    kind: Schema.Literal("presentation"),
    mediaType: Schema.Literal(
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    ),
  }),
  Schema.Struct({
    ...ImportRequestFields,
    kind: Schema.Literal("spreadsheet"),
    mediaType: Schema.Literal("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"),
  }),
  Schema.Struct({
    ...ImportRequestFields,
    kind: Schema.Literal("pdf"),
    mediaType: Schema.Literal("application/pdf"),
  }),
  Schema.Struct({
    ...ImportRequestFields,
    kind: Schema.Literal("markdown"),
    mediaType: Schema.Literal("text/markdown"),
  }),
]);

const ExportRequestFields = {
  schemaVersion: Schema.Literal(1),
  requestId: CanonicalIdSchema,
  workspaceId: CanonicalIdSchema,
  projectId: CanonicalIdSchema,
  artifactId: CanonicalIdSchema,
  revisionId: CanonicalIdSchema,
} as const;
export const OfficeExportRequestSchema = Schema.Union([
  Schema.Struct({
    ...ExportRequestFields,
    kind: Schema.Literal("document"),
    format: Schema.Literals(["docx", "pdf", "md", "html"]),
  }),
  Schema.Struct({
    ...ExportRequestFields,
    kind: Schema.Literal("presentation"),
    format: Schema.Literals(["pptx", "pdf", "png"]),
  }),
  Schema.Struct({
    ...ExportRequestFields,
    kind: Schema.Literal("spreadsheet"),
    format: Schema.Literals(["xlsx", "csv", "pdf"]),
  }),
  Schema.Struct({
    ...ExportRequestFields,
    kind: Schema.Literal("pdf"),
    format: Schema.Literals(["pdf", "docx", "pptx", "xlsx"]),
  }),
  Schema.Struct({
    ...ExportRequestFields,
    kind: Schema.Literal("markdown"),
    format: Schema.Literals(["md", "docx", "pdf", "html"]),
  }),
]);

export const OfficePublicErrorCodes = [
  "OFFICE_INVALID_INPUT",
  "OFFICE_FORBIDDEN",
  "OFFICE_NOT_FOUND",
  "OFFICE_REVISION_NOT_READY",
  "OFFICE_INTEGRITY_ERROR",
  "OFFICE_UNAVAILABLE",
  "OFFICE_STORAGE_QUOTA_EXCEEDED",
  "OFFICE_RATE_LIMITED",
  "OFFICE_UNSUPPORTED_FORMAT",
  "OFFICE_TARGET_NOT_FOUND",
  "OFFICE_OPERATION_UNSUPPORTED",
  "OFFICE_OPERATION_REJECTED",
  "OFFICE_ENCRYPTED_FILE",
  "OFFICE_ARCHIVE_LIMIT_EXCEEDED",
  "OFFICE_MACRO_REJECTED",
  "OFFICE_EXTERNAL_RESOURCE_REJECTED",
  "OFFICE_REVISION_CONFLICT",
  "OFFICE_RENDER_FAILED",
  "OFFICE_CONVERSION_FAILED",
  "OFFICE_CANCELLED",
] as const;
export const OfficePublicErrorCodeSchema = Schema.Literals(OfficePublicErrorCodes);
export const OfficePublicErrorSchema = Schema.Struct({
  code: OfficePublicErrorCodeSchema,
  message: CanonicalTextSchema.check(Schema.isMaxLength(1_000)),
  retryable: Schema.Boolean,
});

export const OfficeArtifactEventSchema = Schema.Union([
  Schema.Struct({
    type: Schema.Literal("office.patch.proposed"),
    operationId: CanonicalIdSchema,
    artifactId: CanonicalIdSchema,
    baseRevisionId: CanonicalIdSchema,
    summary: CanonicalTextSchema.check(Schema.isMaxLength(1_000)),
  }),
  Schema.Struct({
    type: Schema.Literal("office.patch.completed"),
    operationId: CanonicalIdSchema,
    artifactId: CanonicalIdSchema,
    revisionId: CanonicalIdSchema,
    summary: CanonicalTextSchema.check(Schema.isMaxLength(1_000)),
  }),
  Schema.Struct({
    type: Schema.Literal("office.patch.conflict"),
    operationId: CanonicalIdSchema,
    artifactId: CanonicalIdSchema,
    expectedRevisionId: CanonicalIdSchema,
    currentRevisionId: CanonicalIdSchema,
  }),
  Schema.Struct({
    type: Schema.Literal("office.export.completed"),
    requestId: CanonicalIdSchema,
    artifactId: CanonicalIdSchema,
    revisionId: CanonicalIdSchema,
    exportObjectId: CanonicalIdSchema,
    format: CanonicalTextSchema.check(Schema.isMaxLength(16)),
  }),
]);

export type OfficeArtifactKind = typeof OfficeArtifactKindSchema.Type;
export type OfficeArtifactKindEncoded = typeof OfficeArtifactKindSchema.Encoded;
export type OfficeNativeMediaType = typeof OfficeNativeMediaTypeSchema.Type;
export type OfficeNativeMediaTypeEncoded = typeof OfficeNativeMediaTypeSchema.Encoded;
export type OfficeEngine = typeof OfficeEngineSchema.Type;
export type OfficeEngineEncoded = typeof OfficeEngineSchema.Encoded;
export type OfficeRevisionPayload = typeof OfficeRevisionPayloadSchema.Type;
export type OfficeRevisionPayloadEncoded = typeof OfficeRevisionPayloadSchema.Encoded;
export type OfficeSelection = typeof OfficeSelectionSchema.Type;
export type OfficeSelectionEncoded = typeof OfficeSelectionSchema.Encoded;
export type OfficeSelectionTarget = typeof OfficeSelectionTargetSchema.Type;
export type OfficeSelectionTargetEncoded = typeof OfficeSelectionTargetSchema.Encoded;
export type OfficePdfNativePageRef = typeof OfficePdfNativePageRefSchema.Type;
export type OfficePdfNativePageRefEncoded = typeof OfficePdfNativePageRefSchema.Encoded;
export type OfficePdfPageId = typeof OfficePdfPageIdSchema.Type;
export type OfficePdfPageIdEncoded = typeof OfficePdfPageIdSchema.Encoded;
export type OfficePdfHighlightRect = typeof OfficePdfHighlightRectSchema.Type;
export type OfficePdfHighlightRectEncoded = typeof OfficePdfHighlightRectSchema.Encoded;
export type OfficePdfHighlightColor = typeof OfficePdfHighlightColorSchema.Type;
export type OfficePdfHighlightColorEncoded = typeof OfficePdfHighlightColorSchema.Encoded;
export type OfficeDocumentAlignment = typeof OfficeDocumentAlignmentSchema.Type;
export type OfficeDocumentAlignmentEncoded = typeof OfficeDocumentAlignmentSchema.Encoded;
export type OfficeDocumentInlineMark = typeof OfficeDocumentInlineMarkSchema.Type;
export type OfficeDocumentInlineMarkEncoded = typeof OfficeDocumentInlineMarkSchema.Encoded;
export type OfficeDocumentVerticalAlignment = typeof OfficeDocumentVerticalAlignmentSchema.Type;
export type OfficeDocumentVerticalAlignmentEncoded =
  typeof OfficeDocumentVerticalAlignmentSchema.Encoded;
export type OfficeDocumentFontSizePt = typeof OfficeDocumentFontSizePtSchema.Type;
export type OfficeDocumentFontSizePtEncoded = typeof OfficeDocumentFontSizePtSchema.Encoded;
export type OfficeFontFamily = typeof OfficeFontFamilySchema.Type;
export type OfficeFontFamilyEncoded = typeof OfficeFontFamilySchema.Encoded;
export type OfficeDocumentTextColor = typeof OfficeDocumentTextColorSchema.Type;
export type OfficeDocumentTextColorEncoded = typeof OfficeDocumentTextColorSchema.Encoded;
export type OfficeDocumentHighlight = typeof OfficeDocumentHighlightSchema.Type;
export type OfficeDocumentHighlightEncoded = typeof OfficeDocumentHighlightSchema.Encoded;
export type OfficeDocumentTextFormatPatch = typeof OfficeDocumentTextFormatPatchSchema.Type;
export type OfficeDocumentTextFormatPatchEncoded =
  typeof OfficeDocumentTextFormatPatchSchema.Encoded;
export type OfficeDocumentPageSetup = typeof OfficeDocumentPageSetupSchema.Type;
export type OfficeDocumentPageSetupEncoded = typeof OfficeDocumentPageSetupSchema.Encoded;
export type OfficePresentationTextAlignment = typeof OfficePresentationTextAlignmentSchema.Type;
export type OfficePresentationTextAlignmentEncoded =
  typeof OfficePresentationTextAlignmentSchema.Encoded;
export type OfficePresentationTextFormatPatch = typeof OfficePresentationTextFormatPatchSchema.Type;
export type OfficePresentationTextFormatPatchEncoded =
  typeof OfficePresentationTextFormatPatchSchema.Encoded;
export type OfficePresentationPresetShapeKind =
  typeof OfficePresentationPresetShapeKindSchema.Type;
export type OfficePresentationPresetShapeKindEncoded =
  typeof OfficePresentationPresetShapeKindSchema.Encoded;
export type OfficePresentationTransitionKind = typeof OfficePresentationTransitionKindSchema.Type;
export type OfficePresentationTransitionKindEncoded =
  typeof OfficePresentationTransitionKindSchema.Encoded;
export type OfficePresentationSlideTransition = typeof OfficePresentationSlideTransitionSchema.Type;
export type OfficePresentationSlideTransitionEncoded =
  typeof OfficePresentationSlideTransitionSchema.Encoded;
export type OfficePresentationAnimationEffect = typeof OfficePresentationAnimationEffectSchema.Type;
export type OfficePresentationAnimationEffectEncoded =
  typeof OfficePresentationAnimationEffectSchema.Encoded;
export type OfficePresentationAnimationTrigger =
  typeof OfficePresentationAnimationTriggerSchema.Type;
export type OfficePresentationAnimationTriggerEncoded =
  typeof OfficePresentationAnimationTriggerSchema.Encoded;
export type OfficePresentationAnimation = typeof OfficePresentationAnimationSchema.Type;
export type OfficePresentationAnimationEncoded = typeof OfficePresentationAnimationSchema.Encoded;
export type OfficeSpreadsheetHorizontalAlignment =
  typeof OfficeSpreadsheetHorizontalAlignmentSchema.Type;
export type OfficeSpreadsheetHorizontalAlignmentEncoded =
  typeof OfficeSpreadsheetHorizontalAlignmentSchema.Encoded;
export type OfficeSpreadsheetNumberFormat = typeof OfficeSpreadsheetNumberFormatSchema.Type;
export type OfficeSpreadsheetNumberFormatEncoded =
  typeof OfficeSpreadsheetNumberFormatSchema.Encoded;
export type OfficeSpreadsheetSheetName = typeof OfficeSpreadsheetSheetNameSchema.Type;
export type OfficeSpreadsheetSheetNameEncoded = typeof OfficeSpreadsheetSheetNameSchema.Encoded;
export type OfficeSpreadsheetHyperlinkTarget = typeof OfficeSpreadsheetHyperlinkTargetSchema.Type;
export type OfficeSpreadsheetHyperlinkTargetEncoded =
  typeof OfficeSpreadsheetHyperlinkTargetSchema.Encoded;
export type OfficeSpreadsheetCellFormatPatch = typeof OfficeSpreadsheetCellFormatPatchSchema.Type;
export type OfficeSpreadsheetCellFormatPatchEncoded =
  typeof OfficeSpreadsheetCellFormatPatchSchema.Encoded;
export type OfficeArtifactPatchEnvelope = typeof OfficeArtifactPatchEnvelopeSchema.Type;
export type OfficeArtifactPatchEnvelopeEncoded = typeof OfficeArtifactPatchEnvelopeSchema.Encoded;
export type OfficePatchableArtifactPatchEnvelope =
  typeof OfficePatchableArtifactPatchEnvelopeSchema.Type;
export type OfficePatchableArtifactPatchEnvelopeEncoded =
  typeof OfficePatchableArtifactPatchEnvelopeSchema.Encoded;
export type OfficeNativeIdentityAlias = typeof OfficeNativeIdentityAliasSchema.Type;
export type OfficeNativeIdentityAliasEncoded = typeof OfficeNativeIdentityAliasSchema.Encoded;
export type OfficeImportRequest = typeof OfficeImportRequestSchema.Type;
export type OfficeImportRequestEncoded = typeof OfficeImportRequestSchema.Encoded;
export type OfficeExportRequest = typeof OfficeExportRequestSchema.Type;
export type OfficeExportRequestEncoded = typeof OfficeExportRequestSchema.Encoded;
export type OfficePublicError = typeof OfficePublicErrorSchema.Type;
export type OfficePublicErrorEncoded = typeof OfficePublicErrorSchema.Encoded;
export type OfficePublicErrorCode = typeof OfficePublicErrorCodeSchema.Type;
export type OfficeArtifactEvent = typeof OfficeArtifactEventSchema.Type;
export type OfficeArtifactEventEncoded = typeof OfficeArtifactEventSchema.Encoded;

export const decodeOfficeArtifactPatchEnvelope = Schema.decodeUnknownEffect(
  OfficeArtifactPatchEnvelopeSchema,
  OfficeBoundaryParseOptions,
);
export const decodeOfficePdfNativePageRef = Schema.decodeUnknownSync(
  OfficePdfNativePageRefSchema,
  OfficeBoundaryParseOptions,
);
export const decodeOfficePdfPageId = Schema.decodeUnknownSync(
  OfficePdfPageIdSchema,
  OfficeBoundaryParseOptions,
);
export function formatOfficePdfPageId(ref: OfficePdfNativePageRef): OfficePdfPageId {
  const canonical = decodeOfficePdfNativePageRef(ref);
  return decodeOfficePdfPageId(`pdf-ref:${canonical.num}:${canonical.gen}`);
}
export function parseOfficePdfPageId(pageId: OfficePdfPageId): OfficePdfNativePageRef {
  return decodeOfficePdfNativePageRef(pdfPageRefFromId(decodeOfficePdfPageId(pageId)));
}
export const decodeOfficePatchableArtifactPatchEnvelope = Schema.decodeUnknownSync(
  OfficePatchableArtifactPatchEnvelopeSchema,
  OfficeBoundaryParseOptions,
);
export const decodeOfficeImportRequest = Schema.decodeUnknownEffect(
  OfficeImportRequestSchema,
  OfficeBoundaryParseOptions,
);
export const decodeOfficeExportRequest = Schema.decodeUnknownEffect(
  OfficeExportRequestSchema,
  OfficeBoundaryParseOptions,
);
export const encodeOfficeRevisionPayload = Schema.encodeSync(OfficeRevisionPayloadSchema);
export const encodeOfficeArtifactEvent = Schema.encodeSync(OfficeArtifactEventSchema);
